// !!! IMPORTANT !!!
// !!! This same logic is used in the backend when planning reminders.
// !!! If you update the frontend, remember to update the backend as well.
// !!! Keep the frontend as the source of truth in this case.
// !!! IMPORTANT !!!

import { getExpiredConfirmationOffsets, getExpiredReminderOffsets } from 'src/modules/event/utils/helpers'

type ReviewTimingIssuesLogicProps = {
  confirmation_enabled?: boolean
  confirmation_offsets?: number[]
  reminder_enabled?: boolean
  reminder_offsets?: number[]
  event_date: Date
}

export interface ReviewTimingIssuesLogicResult {
  hasConfirmation: boolean
  hasReminder: boolean
  allClear: boolean
  noTimeForConfirmation: boolean
  noTimeForReminder: boolean
  overlap: boolean
  offerImmediateConfirmation: boolean
  offerImmediateReminder: boolean
  warnConfirmationWillNotSend: boolean
  warnOverlap: boolean
}

export class ReviewTimingIssuesLogic {
  static evaluate({
    confirmation_enabled,
    confirmation_offsets,
    reminder_enabled,
    reminder_offsets,
    event_date,
  }: ReviewTimingIssuesLogicProps): ReviewTimingIssuesLogicResult {
    if (typeof event_date === 'string' || typeof event_date === 'number') {
      event_date = new Date(event_date)
    }
    if (isNaN(event_date.getTime())) {
      throw new Error('Invalid event date')
    }

    confirmation_offsets = confirmation_offsets ?? []
    reminder_offsets = reminder_offsets ?? []

    const hasConfirmation = Boolean(confirmation_enabled) && confirmation_offsets.length > 0
    const hasReminder = Boolean(reminder_enabled) && reminder_offsets.length > 0

    // Check if the confirmation message is expired
    const expiredConfirmationOffsets =
      hasConfirmation && getExpiredConfirmationOffsets(event_date, confirmation_offsets)
    const noTimeForConfirmation = expiredConfirmationOffsets.length > 0

    // Check if the reminder message is expired
    const expiredReminderOffsets = hasReminder && getExpiredReminderOffsets(event_date, reminder_offsets)
    const noTimeForReminder = expiredReminderOffsets.length > 0

    // Check if the confirmation message is sent after the reminder message
    const overlap =
      hasConfirmation &&
      !noTimeForConfirmation &&
      hasReminder &&
      !noTimeForReminder &&
      confirmation_offsets.some((offset) => {
        const confirmationTime = new Date(new Date().getTime() + offset)
        return reminder_offsets.some((reminderOffset) => {
          const reminderTime = new Date(event_date.getTime() - reminderOffset)
          return confirmationTime > reminderTime
        })
      })

    return {
      hasConfirmation,
      hasReminder,
      allClear: !noTimeForConfirmation && !noTimeForReminder && !overlap,
      noTimeForConfirmation,
      noTimeForReminder,
      overlap,
      offerImmediateConfirmation: hasConfirmation && !hasReminder && noTimeForConfirmation,
      offerImmediateReminder: hasReminder && noTimeForReminder,
      warnConfirmationWillNotSend: hasConfirmation && hasReminder && (noTimeForReminder || noTimeForConfirmation),
      warnOverlap: overlap,
    }
  }

  static getMinExpiredConfirmationOffset(eventDate: Date, selectedOffsets: number[]): number {
    const validOffsets = (selectedOffsets ?? []).filter(Boolean)
    const expiredOffsets = getExpiredConfirmationOffsets(eventDate, validOffsets)
    return expiredOffsets.length ? Math.min(...expiredOffsets) : 0
  }

  static getMinExpiredReminderOffset(eventDate: Date, selectedOffsets: number[]): number {
    // Suppose the event starts in 35 minutes, and the user has set reminders for 1d, 1h and 30m
    // We want to return the smallest offset that is in the past, which is 1h
    // This will cause the modal to show: "1h reminder is in the past, set a reminder for 30m or less"
    const validOffsets = (selectedOffsets ?? []).filter(Boolean)
    const expiredOffsets = getExpiredReminderOffsets(eventDate, validOffsets)
    return expiredOffsets.length ? Math.min(...expiredOffsets) : 0
  }
}
